import React from 'react';
import './MetricsChart.css';

const MetricsChart = ({ metrics }) => {
  if (!metrics || !metrics.data_collector) {
    return (
      <div className="metrics-loading">
        <div className="loading-spinner"></div>
        <p>Загрузка метрик...</p>
      </div>
    );
  }

  const { data_collector } = metrics;
  const dcMetrics = data_collector.metrics || {};

  const formatNumber = (num) => {
    if (num === undefined || num === null) return '0';
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  const formatDuration = (seconds) => {
    if (seconds === undefined || seconds === null || seconds === 0) return '—';
    if (seconds < 1) return `${(seconds * 1000).toFixed(0)}ms`;
    return `${seconds.toFixed(2)}s`;
  };

  const getCycleStatus = (duration) => {
    if (duration === 0 || duration === undefined) return 'unknown';
    if (duration < 5) return 'excellent';
    if (duration < 15) return 'good';
    if (duration < 30) return 'warning';
    return 'critical';
  };

  const cycleStatus = getCycleStatus(dcMetrics.last_cycle_duration);

  return (
    <div className="metrics-chart">
      {/* Status Badge */}
      <div className="dc-status-banner">
        <div className={`status-indicator ${data_collector.running ? 'running' : 'stopped'}`}>
          <span className="status-pulse"></span>
          <span className="status-text">
            {data_collector.running ? 'DataCollector Running' : 'DataCollector Stopped'}
          </span>
        </div>
        {data_collector.timestamp && (
          <span className="last-update">
            Обновлено: {new Date(data_collector.timestamp).toLocaleTimeString()}
          </span>
        )}
      </div>

      {/* Performance Metrics Grid */}
      <div className="performance-grid">
        {/* Cycle Duration - Main Metric */}
        <div className={`perf-card cycle-card ${cycleStatus}`}>
          <div className="perf-header">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <polyline points="12 6 12 12 16 14"/>
            </svg>
            <span className="perf-title">Cycle Duration</span>
          </div>
          <div className="perf-value-large">
            {formatDuration(dcMetrics.last_cycle_duration)}
          </div>
          <div className="perf-subtitle">
            {cycleStatus === 'excellent' && 'Отличная производительность'}
            {cycleStatus === 'good' && 'Хорошая производительность'}
            {cycleStatus === 'warning' && 'Требует внимания'}
            {cycleStatus === 'critical' && 'Критическое время'}
            {cycleStatus === 'unknown' && 'Ожидание данных...'}
          </div>
          <div className="cycle-bar">
            <div 
              className="cycle-bar-fill" 
              style={{ 
                width: `${Math.min((dcMetrics.last_cycle_duration || 0) / 30 * 100, 100)}%` 
              }}
            />
            <div className="cycle-bar-markers">
              <span>0s</span>
              <span>15s</span>
              <span>30s+</span>
            </div>
          </div>
        </div>

        {/* Active Strategies */}
        <div className="perf-card">
          <div className="perf-header">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
            </svg>
            <span className="perf-title">Active Strategies</span>
          </div>
          <div className="perf-value">{dcMetrics.active_strategies || 0}</div>
          <div className="perf-subtitle">Активных стратегий</div>
        </div>

        {/* Pairs in Collection */}
        <div className="perf-card">
          <div className="perf-header">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="1" x2="12" y2="23"/>
              <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
            </svg>
            <span className="perf-title">Pairs Collected</span>
          </div>
          <div className="perf-value">{dcMetrics.pairs_in_collection || 0}</div>
          <div className="perf-subtitle">Торговых пар</div>
        </div>

        {/* Active Exchanges */}
        <div className="perf-card">
          <div className="perf-header">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
              <line x1="8" y1="21" x2="16" y2="21"/>
              <line x1="12" y1="17" x2="12" y2="21"/>
            </svg>
            <span className="perf-title">Exchanges</span>
          </div>
          <div className="perf-value">{dcMetrics.active_exchanges || 0}</div>
          <div className="perf-subtitle">Активных бирж</div>
        </div>
      </div>

      {/* API Requests Stats */}
      <div className="api-stats">
        <h4 className="api-stats-title">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
            <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
            <line x1="12" y1="22.08" x2="12" y2="12"/>
          </svg>
          API Requests
        </h4>
        <div className="api-stats-grid">
          <div className="api-stat">
            <div className="api-stat-icon candles">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="20" x2="18" y2="10"/>
                <line x1="12" y1="20" x2="12" y2="4"/>
                <line x1="6" y1="20" x2="6" y2="14"/>
              </svg>
            </div>
            <div className="api-stat-content">
              <span className="api-stat-value">{formatNumber(dcMetrics.api_requests_candles)}</span>
              <span className="api-stat-label">Candles</span>
            </div>
          </div>
          
          <div className="api-stat">
            <div className="api-stat-icon tickers">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/>
                <polyline points="16 7 22 7 22 13"/>
              </svg>
            </div>
            <div className="api-stat-content">
              <span className="api-stat-value">{formatNumber(dcMetrics.api_requests_tickers)}</span>
              <span className="api-stat-label">Tickers</span>
            </div>
          </div>
          
          <div className="api-stat">
            <div className="api-stat-icon all-tickers">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="2" y1="12" x2="22" y2="12"/>
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
              </svg>
            </div>
            <div className="api-stat-content">
              <span className="api-stat-value">{formatNumber(dcMetrics.api_requests_all_tickers)}</span>
              <span className="api-stat-label">All Tickers</span>
            </div>
          </div>
        </div>
      </div>

      {/* Cache Performance */}
      <div className="cache-perf">
        <h4 className="cache-perf-title">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
          </svg>
          Cache Performance
        </h4>
        <div className="cache-perf-grid">
          <div className="cache-perf-item hits">
            <span className="cache-perf-value">{formatNumber(dcMetrics.cache_hits)}</span>
            <span className="cache-perf-label">Cache Hits</span>
          </div>
          <div className="cache-perf-item misses">
            <span className="cache-perf-value">{formatNumber(dcMetrics.cache_misses)}</span>
            <span className="cache-perf-label">Cache Misses</span>
          </div>
          <div className="cache-perf-item size">
            <span className="cache-perf-value">{dcMetrics.cache_size || 0}</span>
            <span className="cache-perf-label">Cache Size</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MetricsChart;
